import { useEffect, useState } from "react";
import { UseFormReturn } from "react-hook-form";

import { ZodProvider } from "@autoform/zod";
import {
  useNavigation,
  useNotification,
  useParsed,
  useWarnAboutChange,
} from "@refinedev/core";
import { useDocumentTitle } from "@refinedev/react-router";

import { BottomActionBar } from "@/components/bottom-action-bar";
import { useJustCreated } from "@/components/common/enrichment-async-hint";
import { CuratedFieldsProvider } from "@/components/field-curation/curated-fields-context";
import { DeleteButton } from "@/components/refine-ui/buttons/delete";
import { AutoForm } from "@/components/ui/autoform";
import { injectCustomAutoFormComponents } from "@/components/ui/autoform/components/custom";
import { Button } from "@/components/ui/button";
import { useLoadingOverlay } from "@/contexts/loading-context";
import { toHumanErrorMessage } from "@/lib/extensions/httpErrorExtension";
import { getChangedValues, withClearedFields } from "@/lib/utils";
import { ArtistMergeButton } from "@/pages/artists/components/artist-merge-button";
import { ArtistPreview } from "@/pages/artists/components/artist-preview";
import { ArtistUnmergeButton } from "@/pages/artists/components/artist-unmerge-button";
import { useArtistRepository } from "@/pages/artists/hooks/use-artist-repository";
import { ArtistViewType } from "@/pages/artists/schemas/artist";
import {
  ArtistEditFormType,
  artistEditSchema,
} from "@/pages/artists/schemas/artist.edit.form";

const schemaProvider = new ZodProvider(artistEditSchema);

/**
 * Fields a curator may clear, i.e. the ones the API accepts `""` for.
 * `about` is the only one: there is no `UpdateArtistCommandValidator`, so no
 * server-side format rule can reject `""`, and the handler `Set()`s it through.
 * `status` is an enum (`ArtistStatusDb?` — `""` fails JSON binding → 400), and
 * `slug` is not even a property on `UpdateArtistCommand`, so sending it does
 * nothing at all.
 */
const CLEARABLE_FIELDS = ["about"] as const;

export const ArtistsEdit = () => {
  const { get, update } = useArtistRepository();
  const { id } = useParsed();
  const [values, setValues] = useState<ArtistViewType>();
  const { open } = useNotification();
  const { setLoading } = useLoadingOverlay();
  const { setWarnWhen, warnWhen } = useWarnAboutChange();
  const { list } = useNavigation();
  const justCreated = useJustCreated();

  const setTitle = useDocumentTitle();

  const loadArtist = () => {
    if (!id) return;
    setLoading(true);
    get(id.toString())
      .then((response) => {
        setValues(response);
        setTitle("Edit " + response.name);
      })
      .catch((error) => {
        console.error(error);
        open?.({
          type: "error",
          message: "Failed to get artist",
          description: toHumanErrorMessage(error),
        });
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!id) {
      open?.({
        type: "error",
        message: "Something went wrong. This shouldn't be happening",
      });
      return;
    }
    loadArtist();
  }, [id]);

  const initializeForm = (
    form: UseFormReturn<ArtistEditFormType, any, ArtistEditFormType>,
  ) => {
    form.watch((changedValues) => {
      if (!values) return;
      const changed = getChangedValues(
        changedValues,
        values as ArtistEditFormType,
      );
      console.debug("Form Changed", changed);
      setWarnWhen(Object.keys(changed).length > 0);
    });
  };

  const onSubmit = async (
    allValues: ArtistEditFormType,
    form: UseFormReturn<ArtistEditFormType, any, ArtistEditFormType>,
  ) => {
    const changed = getChangedValues(
      allValues,
      form.formState.defaultValues,
    ) as Partial<ArtistEditFormType>;
    // AutoForm strips emptied fields before we get here — re-add the ones the
    // curator cleared on purpose as "", which the API stores as an empty value.
    const dirty = withClearedFields<ArtistEditFormType>(
      changed,
      form.getValues() as Record<string, unknown>,
      form.formState.defaultValues as Record<string, unknown>,
      CLEARABLE_FIELDS,
    );
    setWarnWhen(false);
    await update({ id: allValues.id, ...dirty });
    // A human save can itself mark fields as curated (the backend curates
    // every field that changed). This is an info-only marker, so the new
    // ones showing up only on the next load — rather than re-reading here —
    // is acceptable; it avoids remounting the form (which would drop
    // anything the curator has typed since clicking Submit).
  };

  return (
    <div className="flex flex-row">
      <div className="w-3/4 p-8 flex flex-col space-y-4">
        {values?.mergedInto && (
          <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm">
            Merged into{" "}
            <a
              className="underline"
              href={`/artists/edit/${values.mergedInto}`}
            >
              {values.mergedInto}
            </a>
            . This artist is hidden everywhere and has no events. Unmerge to
            restore it, or delete it if it is definitely redundant.
          </div>
        )}
        {values && (
          <CuratedFieldsProvider curated={values.curatedFields}>
            <AutoForm
              key={JSON.stringify(values)}
              schema={schemaProvider}
              defaultValues={values as ArtistEditFormType}
              onSubmit={onSubmit}
              onFormInit={initializeForm}
              {...injectCustomAutoFormComponents()}
            >
              <BottomActionBar className="gap-2">
                <Button disabled={!warnWhen} type="submit">
                  Submit
                </Button>
                <div className="flex-1" />
                {values.slug && (
                  <Button
                    type="button"
                    onClick={() =>
                      window.open(
                        `https://events.musicofourdesire.com/artist/${values.slug}`,
                        "_blank",
                      )
                    }
                  >
                    View in web
                  </Button>
                )}
                {values.mergedInto ? (
                  <ArtistUnmergeButton id={values.id} onUnmerged={loadArtist} />
                ) : (
                  <ArtistMergeButton id={values.id} />
                )}
                <DeleteButton
                  resource="artists"
                  onSuccess={() => {
                    list("artists");
                  }}
                />
              </BottomActionBar>
            </AutoForm>
          </CuratedFieldsProvider>
        )}
      </div>
      {values && (
        <ArtistPreview
          artist={values}
          onArtistChanged={loadArtist}
          justCreated={justCreated}
        />
      )}
    </div>
  );
};
